define([
    'lodash',
    'componentsCore',
    'textCommon',
    'santa-components',
    'coreUtils',
    'datePicker/subComponents/calendar'
], function (_, componentsCore, textCommon, santaComponents, coreUtils, calendar) {
    'use strict';

    const labelUtils = textCommon.labelUtils;

    function getSelectedDateFromProps(defaultTextType) {
        let selectedDate = null;

        if (defaultTextType === 'today') {
            if (_.get(this, 'dateUtils')) {
                selectedDate = this.dateUtils.helpers.getStartOfToday();
            } else {
                selectedDate = new Date();
                selectedDate.setHours(0, 0, 0, 0);
            }
        }

        return selectedDate;
    }

    const getPublicState = function (state, propsInfo) {
        const defaultTextType = _.get(propsInfo, 'props.defaultTextType');

        return _.assign({
            selectedDate: _.get(state, 'selectedDate', getSelectedDateFromProps.call(this, defaultTextType))
        }, santaComponents.mixins.validatableWithSyncMixin.getPublicState(state));
    };

    /**
     * @class components.DatePicker
     * @extends {core.skinBasedComp}
     */
    return {
        displayName: 'DatePicker',
        propTypes: _.assign({
            compData: santaComponents.santaTypesDefinitions.Component.compData.isRequired,
            compProp: santaComponents.santaTypesDefinitions.Component.compProp.isRequired,
            id: santaComponents.santaTypesDefinitions.Component.id.isRequired,
            styleId: santaComponents.santaTypesDefinitions.Component.styleId.isRequired,
            shouldResetComponent: santaComponents.santaTypesDefinitions.RenderFlags.shouldResetComponent.isRequired,
            componentPreviewState: santaComponents.santaTypesDefinitions.RenderFlags.componentPreviewState,
            windowClickEventAspect: santaComponents.santaTypesDefinitions.SiteAspects.windowClickEventAspect.isRequired,
            isMobileView: santaComponents.santaTypesDefinitions.isMobileView.isRequired,
            isMobileDevice: santaComponents.santaTypesDefinitions.Device.isMobileDevice.isRequired
        }, santaComponents.utils.santaTypesUtils.getSantaTypesByDefinition(calendar)),
        statics: {
            behaviors: _.assign({setValue: {methodName: 'setValue', params: ['value']}},
                santaComponents.mixins.validatableWithSyncMixin.VALIDATABLE_WITH_SYNC_BEHAVIORS,
                santaComponents.mixins.inputFocusMixin.INPUT_FOCUS_BEHAVIORS
            )
        },
        mixins: [
            componentsCore.mixins.skinBasedComp,
            santaComponents.mixins.runTimeCompData,
            componentsCore.mixins.createChildComponentMixin,
            santaComponents.mixins.inputFocusMixin,
            textCommon.textScaleMixin,
            santaComponents.mixins.validatableWithSyncMixin.validatableWithSync,
            santaComponents.mixins.compStateMixin(getPublicState)
        ],
        getInitialState() {
            return _.assign(getPublicState(), {
                shouldShowCalendar: false,
                dateUtilsInit: false,
                selectedDate: getSelectedDateFromProps.call(this, this.props.compProp.defaultTextType),
                $mobile: this.props.isMobileDevice || this.props.isMobileView ? 'mobile' : 'notMobile'
            });
        },
        setValue(value) {
            this.setState({selectedDate: value});
        },
        focus() {
            this.refs.input.focus();
        },
        blur() {
            this.refs.input.blur();
        },
        componentWillReceiveProps(nextProps) {
            const newState = {};
            const shouldUpdateSelectedDate = nextProps.compProp.defaultTextType !== this.props.compProp.defaultTextType;
            const shouldResetComponent = nextProps.shouldResetComponent && this.props.shouldResetComponent !== nextProps.shouldResetComponent;

            if (shouldResetComponent) {
                this.hideValidityIndication();
                this.closeCalendar();
                _.assign(newState, {
                    selectedDate: getSelectedDateFromProps.call(this, nextProps.compProp.defaultTextType)
                });
            }

            if (shouldUpdateSelectedDate && !shouldResetComponent) {
                _.assign(newState, {selectedDate: getSelectedDateFromProps.call(this, nextProps.compProp.defaultTextType)});
            }

            if (!_.isEmpty(newState)) {
                this.setState(newState);
            }
        },
        componentDidMount() {
            this._isMounted = true;
            this.props.windowClickEventAspect.registerToDocumentClickEvent(this.props.id);
            const self = this;
            requirejs(['dateUtils'], function (dateUtils) {
                if (self._isMounted) {
                    self.dateUtils = dateUtils;
                    self.setState({dateUtilsInit: true});
                }
            });
        },
        componentWillUnmount() {
            this._isMounted = false;
            this.props.windowClickEventAspect.unRegisterToDocumentClickEvent(this.props.id);
        },
        onDocumentClick(element) {
            const targetClassName = element.target.className;
            const targetId = element.target.id;
            const styleId = this.props.styleId;
            const isDatePickerClicked = _.includes(targetId, this.props.id) && _.includes(targetClassName, `${styleId}input`);
            const isCalendarClicked = this.props.isMobileDevice || this.props.isMobileView ?
                !_.isEqual(targetId, `${this.props.id}calendar`) :
                _.includes(targetClassName, `${styleId}calendar`);

            if (this.state.shouldShowCalendar && !isDatePickerClicked && !isCalendarClicked) {
                this.handleInputBlur();
            }
        },
        openCalendar(callback) {
            this.setState({shouldShowCalendar: true}, callback);
        },
        closeCalendar(callback) {
            this.setState({shouldShowCalendar: false}, callback);
        },
        toggleCalendar() {
            if (this.state.shouldShowCalendar) {
                this.handleInputBlur();
            } else {
                this.handleInputFocus();
            }
        },
        onDateChange(newDate) {
            if (!this.dateUtils.helpers.isEqual(this.state.selectedDate, newDate)) {
                const newState = {
                    selectedDate: newDate
                };

                this.setState(newState, () => {
                    this.handleAction(coreUtils.siteConstants.ACTION_TYPES.CHANGE);
                });
            }
        },
        isPreviewState(state) {
            return _.includes(this.props.componentPreviewState, state);
        },
        getCalendarProperties() {
            const startOfToday = this.dateUtils.helpers.getStartOfToday();

            return {
                id: `${this.props.id}calendar`,
                ref: `${this.props.id}calendar`,
                style: {},
                dateUtils: this.dateUtils,
                calendarDate: this.state.selectedDate || startOfToday,
                today: startOfToday,
                closeCalendar: this.closeCalendar,
                onDateChange: this.onDateChange,
                allowPastDates: this.props.compData.allowPastDates,
                allowFutureDates: this.props.compData.allowFutureDates,
                disabledDates: this.props.compData.disabledDates,
                disabledDaysOfWeek: this.props.compData.disabledDaysOfWeek,
                minDate: this.props.compData.minDate,
                maxDate: this.props.compData.maxDate,
                selectedDate: this.state.selectedDate
            };
        },
        getCalendar() {
            const shouldRenderCalendar = this.state.shouldShowCalendar && this.state.dateUtilsInit;
            return shouldRenderCalendar ? this.createChildComponent(
                {},
                'wysiwyg.viewer.components.Calendar',
                'calendar',
                this.getCalendarProperties()
            ) : {};
        },
        getDateInputText() {
            let inputText = '';
            if (this.state.selectedDate && this.state.dateUtilsInit) {
                inputText = this.dateUtils.helpers.getDateByFormat(this.state.selectedDate, this.props.compProp.dateFormat);
            }

            return inputText;
        },
        getPlaceholderText() {
            const placeholder = this.props.compData.placeholder || this.props.compProp.placeholder;
            return this.state.selectedDate || !placeholder ? '' : placeholder;
        },
        getHostSkinProperties() {
            const {compData, compProp, compTheme} = this.props;
            const classSet = {
                focused: this.state.shouldShowCalendar,
                'read-only': !!this.props.compProp.readOnly,
                invalid: !this.isValid(),
                'with-validation-indication': this.shouldShowValidityIndication(),
                'required': labelUtils.showRequired(compData, compProp, compTheme)
            };
            classSet[`${this.props.compProp.textAlignment}-direction`] = true;

            return {
                className: this.classSet(classSet),
                'data-disabled': !!this.props.compProp.isDisabled
            };
        },
        handleInputFocus(event) {
            this.openCalendar(this.handleAction.bind(this, coreUtils.siteConstants.ACTION_TYPES.FOCUS, event));
        },
        handleInputBlur(event) {
            this.closeCalendar(this.handleAction.bind(this, coreUtils.siteConstants.ACTION_TYPES.BLUR, event));
        },
        getInputSkinProperties() {
            const isRequired = !!this.props.compProp.required;
            const isReadOnly = !!this.props.compProp.readOnly;
            const isDisabled = !!this.props.compProp.isDisabled;
            const textPadding = this.props.compProp.textPadding;
            const textAlignment = this.props.compProp.textAlignment;
            const padding = textAlignment === 'right' ? {paddingRight: textPadding} : {paddingLeft: textPadding};

            return {
                value: this.getDateInputText(),
                placeholder: this.getPlaceholderText(),
                onFocus: !isReadOnly && !isDisabled ? this.handleInputFocus : null,
                onBlur: !isReadOnly && !isDisabled ? this.handleInputBlur : null,
                onChange: () => {},
                readOnly: isReadOnly,
                required: isRequired,
                disabled: isDisabled,
                style: _.merge(this.getFontSize(), padding),
                className: 'has-custom-focus'
            };
        },
        getSkinProperties() {
            const {compProp, isMobileView, compData} = this.props;
            const id = {compId: this.props.id, inputId: 'input'};
            return _.merge(labelUtils.getBaseInputWithLabelSkinProperties({compProp, rawFontSyle: this.getFontSize('fntlbl'), isMobileView, compData, id}), {
                '': this.getHostSkinProperties(),
                inputWrapper: {
                    onClick: !this.state.dateUtilsInit ||
                    this.props.compProp.isDisabled ||
                    this.props.compProp.readOnly ? null : this.toggleCalendar,
                    style: {height: labelUtils.hasLabel(this.props.compData) ? `${this.props.compProp.inputHeight}px` : '100%'}
                },
                input: this.getInputSkinProperties(),
                calendar: this.getCalendar()
            });
        },

        measureComponent(node) {
            const {icon, input, inputWrapper, label} = this.refs;
            const getComputedStyle = node.ownerDocument.defaultView.getComputedStyle;
            const iconWidthWithPadding = 56;

            const inputTextLineHeight = parseInt(getComputedStyle(input).lineHeight, 10) || 0;
            const borderWidth = parseInt(getComputedStyle(inputWrapper).borderWidth, 10) || 0;
            const totalBorderSize = borderWidth * 2;

            const minWidth = iconWidthWithPadding + totalBorderSize;
            const inputWrapperWidth = Math.max(minWidth, inputWrapper.offsetWidth, iconWidthWithPadding);

            const iconHeightWithPadding = icon.height.baseVal.value + 16;
            const minHeight = Math.max(inputTextLineHeight, iconHeightWithPadding) + totalBorderSize + label.offsetHeight;

            const heightWithlabel = labelUtils.measureComponentWithLabel(this.props.compProp, label, this.props.compData);
            const height = heightWithlabel ? Math.max(heightWithlabel, minHeight) : Math.max(minHeight, inputWrapper.offsetHeight, iconHeightWithPadding);
            return [{type: 'css', node, changes: {minHeight, minWidth, width: inputWrapperWidth, height}}];
        }
    };
});
